import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { health } from '../lib/api';
import { APP_VERSION } from '../config';
import { SystemService } from '../lib/corel/actions/system';

type ConnectivityContextType = {
  apiOnline: boolean;
  lastChecked: number | null;
  isChecking: boolean;
  checkHealth: () => Promise<boolean>;
};

const ConnectivityContext = createContext<ConnectivityContextType>({
  apiOnline: true,
  lastChecked: null,
  isChecking: false,
  checkHealth: async () => true,
});

/**
 * Provider de conectividade da API.
 * Monitora periodicamente a saúde da API e expõe estado e ação checkHealth.
 */
export function ConnectivityProvider({ children }: { children: React.ReactNode }) {
  const [apiOnline, setApiOnline] = useState<boolean>(true);
  const [lastChecked, setLastChecked] = useState<number | null>(null);
  const [isChecking, setIsChecking] = useState<boolean>(false);

  // Ref para evitar apiOnline na dependency do checkHealth (previne re-create do interval)
  const apiOnlineRef = useRef(apiOnline);
  apiOnlineRef.current = apiOnline;
  const hasBroadcastInitialStatusRef = useRef(false);

  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    try {
      const result = await health();
      const isOnline = result.message !== 'unavailable';
      setApiOnline(isOnline);
    } catch {
      setApiOnline(false);
    } finally {
      (window as any).__API_ONLINE = apiOnlineRef.current;
      setLastChecked(Date.now());
      setIsChecking(false);
    }
    return apiOnlineRef.current;
  }, []);

  useEffect(() => {
    // Não iniciar health check se não há token (tela de login)
    const hasToken = !!localStorage.getItem('cdr:auth:accessToken') || !!localStorage.getItem('accessToken');
    if (!hasToken) return;

    checkHealth();
    const id = setInterval(checkHealth, 30_000);
    return () => clearInterval(id);
  }, [checkHealth]);

  useEffect(() => {
    (window as any).__API_ONLINE = apiOnline;
    // Evita um app-status redundante no bootstrap. O monitor principal já
    // envia app-status ativo via useCorelConnection.
    if (!hasBroadcastInitialStatusRef.current) {
      hasBroadcastInitialStatusRef.current = true;
      return;
    }

    // Emitir app-status para o host C# apenas quando a conectividade mudar de fato.
    SystemService.sendAppStatus({
      apiOnline,
      appVersion: APP_VERSION,
      timestamp: new Date().toISOString(),
    }).catch(() => {
      // noop - fire-and-forget
    });
  }, [apiOnline]);

  // Responder a pings do host C# (health:ping → health:pong)
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      try {
        const msg = typeof event.data === 'string' ? JSON.parse(event.data as string) : event.data;
        if (msg?.action === 'health:ping') {
          const nonce = msg?.data?.nonce;
          // ✅ ATUALIZADO: Usando SystemService
          SystemService.sendHealthPong({
            nonce,
            apiOnline,
            appVersion: APP_VERSION,
            timestamp: new Date().toISOString()
          });
        }
      } catch {
        // ignorar mensagens inválidas
      }
    };
    (window as any)?.chrome?.webview?.addEventListener?.('message', handler);
    return () => {
      try { (window as any)?.chrome?.webview?.removeEventListener?.('message', handler); } catch {}
    };
  }, [apiOnline]);

  return (
    <ConnectivityContext.Provider value={useMemo(() => ({ apiOnline, lastChecked, isChecking, checkHealth }), [apiOnline, lastChecked, isChecking, checkHealth])}>
      {children}
    </ConnectivityContext.Provider>
  );
}

/**
 * Hook para consumir o estado de conectividade da API.
 * @returns Objeto com apiOnline, lastChecked, isChecking e checkHealth
 */
export function useConnectivity() {
  return useContext(ConnectivityContext);
}


